// Hücre editörü / formül penceresinin kırılım bölümü — SAF türetim (spec §6.2, §18 DR4). Bileşen
// KpiCellBreakdownSection bunu çizer. Durum DTO'dan okunur (resolve `failure` / `missingRefs` /
// `isReported` / `breakdownChecks`; `totalCellState` tek karar), hesap satırı aynı kolondaki üye
// hücrelerinden, doğrulama satırları hesap grubu DIŞI doğrulama gruplarının (`isVerificationGroup`)
// resolve değerinden (seçili kırılımlar ve `rollup='none'` yalnız gösterilir — karşılaştırılmaz). XXX kuralı: onaysız üye / toplam → "XXX",
// ✓/✗ yok. Portal: yalnız durum (grup/boyut adı, doğrulama, üst satırı YOK).

import {
  formatKpiNumber,
  isVerificationGroup,
  type KpiBreakdownCheck,
  type KpiResolvedCell,
  type KpiResolvedIndicator,
} from "@mahir/shared";
import { KPI_BREAKDOWN_COPY as C } from "./kpi-breakdown-copy";
import {
  calcGroupFor,
  isBreakdownDerivedTotal,
  memberRowInfo,
  rowKindOf,
  totalCellState,
  type TotalCellState,
} from "./kpi-grid-breakdown";
import { readCell, type GridColumn } from "./kpi-grid-nav";
import { formatKpiCell, KPI_COPY } from "./kpi-ui";

export type BreakdownEditorRole = "derived_total" | "raw_total" | "member";
/** `ok` ✓ (soluk), `mismatch` ✗ + fark (amber), `partial` seçili kırılımlar (amber), `plain` işaretsiz */
export type BreakdownCheckKind = "ok" | "mismatch" | "partial" | "plain";

export interface BreakdownEditorInfo {
  role: BreakdownEditorRole;
  /** DR4 dört durumundan biri (hücre hesaplanamıyor / raporlanan yedek); yoksa null */
  state: TotalCellState | null;
  /** hesap grubu satırı ("Kırılımdan hesaplanıyor (cinsiyet): …"); hesap grubu yoksa null */
  calc: { line: string; groupLabel: string } | null;
  checks: { text: string; kind: BreakdownCheckKind }[];
  /** (2) üye değeri eksik — [Hücreye git: Erkek 2025] hedefi */
  jumpTo: { indicatorId: string; label: string } | null;
  /** üye hücresi: "Üst: … → … · grup: …" (yalnız danışman) */
  memberLine: string | null;
  /** formülsüz türetilmiş toplamın örtük formülü (resolve `implicitFormula` ile aynı kural) */
  implicitFormula: "TOPLA_ALT()" | "ORT_ALT()" | null;
  /** hücre raporlanan değer taşıyor (K20) — "Raporlanan değer ekle" yerine değiştir yolu */
  reported: boolean;
}

const round6 = (v: number): number => Number(v.toFixed(6));

/** Satır metni: boş "—", onaysız "XXX", aksi hücre biçimleyici (yüzde dahil). */
function cellText(cell: KpiResolvedCell | undefined, percent: boolean): string {
  if (!cell || cell.status === "empty") return C.emptyValue;
  if (cell.unverified === true) return KPI_COPY.cell.pending;
  return formatKpiCell(cell, { percent }).text;
}

const numericOf = (c: KpiResolvedCell | undefined): number | null =>
  c?.status === "filled" && typeof c.value === "number" && c.unverified !== true ? c.value : null;

export function breakdownEditorInfo(
  ind: KpiResolvedIndicator,
  all: readonly KpiResolvedIndicator[],
  col: GridColumn,
  client: boolean,
): BreakdownEditorInfo | null {
  const kids = new Map<string, KpiResolvedIndicator[]>();
  for (const i of all) {
    if (!i.parentId) continue;
    const list = kids.get(i.parentId) ?? [];
    list.push(i);
    kids.set(i.parentId, list);
  }
  const childrenOf = (id: string): KpiResolvedIndicator[] => kids.get(id) ?? [];
  const byId = new Map(all.map((i) => [i.id, i]));
  const percent = ind.valueType === "percent";
  const cell = readCell(ind, col);
  const kind = rowKindOf(ind, childrenOf(ind.id));

  // Üye: üstü grup bandı, onun üstü toplam
  const parent = ind.parentId ? byId.get(ind.parentId) : undefined;
  if (kind === "row" && parent?.nodeKind === "breakdown_group") {
    const total = parent.parentId ? byId.get(parent.parentId) : undefined;
    if (!total) return null;
    return {
      role: "member",
      state: null,
      calc: null,
      checks: [],
      jumpTo: null,
      implicitFormula: null,
      reported: cell?.isReported === true,
      memberLine: client
        ? null
        : C.memberInfo(total.label, cellText(readCell(total, col), percent), parent.label),
    };
  }

  const derived = isBreakdownDerivedTotal(ind, all);
  if (kind !== "total" && !derived) return null;
  const calcGroup = kind === "total" ? calcGroupFor(ind, childrenOf).group : null;
  const calcMembers = calcGroup ? childrenOf(calcGroup.id) : [];
  const state = totalCellState(
    {
      kind,
      total: {
        groups: [],
        memberCount: 0,
        calcMemberCodes: calcMembers.map((m) => m.code),
        derived,
      },
    },
    cell,
  );
  const role: BreakdownEditorRole = derived ? "derived_total" : "raw_total";
  const hasFormula = ind.formulas.some((f) => f.id === ind.activeFormulaId || f.isActive);
  const base = {
    role,
    state,
    implicitFormula:
      derived && !hasFormula
        ? calcGroup?.breakdown?.rollup === "avg"
          ? ("ORT_ALT()" as const)
          : ("TOPLA_ALT()" as const)
        : null,
    reported: cell?.isReported === true,
  };
  if (client) return { ...base, calc: null, checks: [], jumpTo: null, memberLine: null };

  const memberLabel = (m: KpiResolvedIndicator): string =>
    calcGroup ? memberRowInfo(m, calcGroup, ind).label : m.label;
  let calc: BreakdownEditorInfo["calc"] = null;
  let jumpTo: BreakdownEditorInfo["jumpTo"] = null;
  if (derived && calcGroup) {
    const parts = calcMembers.map(
      (m) => `${memberLabel(m)} ${cellText(readCell(m, col), percent)}`,
    );
    if (state?.state === "member_missing_in_set") {
      // TODO-KPI-111: eksik üyeler adıyla ("Erkek (sette yok)"); ad yoksa genel metin
      if (state.missingLabels.length > 0) {
        for (const l of state.missingLabels) parts.push(C.tree.missingMember(l));
      } else parts.push(C.notInSetPart);
    }
    const tail = cell?.status === "filled" && !cell.failure ? ` = ${cellText(cell, percent)}` : "";
    calc = {
      groupLabel: calcGroup.label,
      line: C.computedFrom(
        C.dimensionName(calcGroup.breakdown?.dimension, calcGroup.label),
        parts.join(" + ") + tail,
      ),
    };
    if (state?.state === "member_value_missing") {
      const refs = new Set(cell?.missingRefs ?? []);
      const target =
        calcMembers.find((m) => refs.has(m.code)) ??
        calcMembers.find((m) => readCell(m, col)?.status !== "filled");
      if (target) jumpTo = { indicatorId: target.id, label: memberLabel(target) };
    }
  }

  const totalValue = numericOf(cell);
  const totalPending = cell?.unverified === true || cell?.hasPending === true;
  const checks: BreakdownEditorInfo["checks"] = [];
  for (const g of childrenOf(ind.id)) {
    if (g.nodeKind !== "breakdown_group" || g.id === calcGroup?.id) continue;
    const members = childrenOf(g.id);
    if (members.length === 0) continue;
    const memberCells = members.map((m) => readCell(m, col));
    const unverified = memberCells.some((c) => c?.unverified === true);
    if (g.breakdown?.complete === false) {
      checks.push({
        text: C.checkLinePartial(g.label, partialValue(g, memberCells, percent)),
        kind: "partial",
      });
      continue;
    }
    // tam ama `rollup='none'` grup doğrulama grubu değil (shared `isVerificationGroup`) — resolve
    // `breakdownChecks` üretmez; satır yalnız bilgi (değer yok, oranlar toplanmaz)
    if (!isVerificationGroup(g)) {
      checks.push({ text: C.checkLineDisplayOnly(g.label), kind: "plain" });
      continue;
    }
    const ch = cell?.breakdownChecks?.find((c) => c.groupMetricId === g.metricId);
    if (!ch) continue;
    const dim = C.dimensionName(g.breakdown?.dimension, g.label);
    checks.push(
      checkLine(g.label, dim, ch, { derived, unverified, totalPending, totalValue, percent }),
    );
  }
  return { ...base, calc, checks, jumpTo, memberLine: null };
}

/**
 * Seçili (complete=false) grubun gösterim değeri — yalnız üyelerin hepsi dolu/onaylıyken sayı;
 * sette üyesi olmayan grup "—" (boş toplam "0" değildir). Test için dışa açık.
 */
export function partialValue(
  g: KpiResolvedIndicator,
  cells: readonly (KpiResolvedCell | undefined)[],
  percent: boolean,
): string {
  if (cells.length === 0) return C.emptyValue;
  if (cells.some((c) => c?.unverified === true)) return KPI_COPY.cell.pending;
  const nums = cells.map(numericOf);
  if (nums.some((n) => n === null)) return C.emptyValue;
  const sum = (nums as number[]).reduce((a, b) => a + b, 0);
  const v = g.breakdown?.rollup === "avg" ? sum / nums.length : sum;
  return formatKpiNumber(round6(v), percent ? { percent: true } : {});
}

function checkLine(
  label: string,
  dim: string,
  ch: KpiBreakdownCheck,
  o: {
    derived: boolean;
    unverified: boolean;
    totalPending: boolean;
    totalValue: number | null;
    percent: boolean;
  },
): { text: string; kind: BreakdownCheckKind } {
  const fmt = (v: number): string => formatKpiNumber(v, o.percent ? { percent: true } : {});
  const plain = (text: string) =>
    o.derived ? C.checkLine(label, text, false) : C.rawTotalInfo(dim, text);
  if (o.unverified || ch.status === "pending_xxx")
    return { text: plain(KPI_COPY.cell.pending), kind: "plain" };
  if (ch.status !== "filled" || typeof ch.value !== "number")
    return { text: plain(C.emptyValue), kind: "plain" };
  const value = fmt(ch.value);
  // ham üstte doğrulama grubu olmaz (üyeli aday hesap grubu olurdu); `rollup:none` buraya gelmez
  // (yukarıda yalnız gösterim satırı) — ikisi de savunma olarak kalır
  if (!o.derived || ch.rollup === "none" || o.totalPending || o.totalValue === null)
    return { text: plain(value), kind: "plain" };
  if (round6(ch.value) === round6(o.totalValue))
    return { text: C.checkLine(label, value, true), kind: "ok" };
  const diff = fmt(round6(ch.value - o.totalValue));
  return { text: C.checkLineMismatch(label, value, diff), kind: "mismatch" };
}
